import { Grid, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { FieldArray, FieldArrayRenderProps } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { memo } from "react";
import { DefaultLanguageDataState } from "reduxState/translations/translationsSlice";

interface Props {
  translations: { description?: string; locale?: string; name?: string }[];
  availableLanguages: DefaultLanguageDataState[];
}

export const NameSection = memo(
  ({ translations, availableLanguages }: Props) => {
    const { message } = useMessage();
    const contextMenu = useContextMenu();
    const areAllLanguagesAdded =
      translations?.length === availableLanguages?.length;

    return (
      <>
        <FieldArray
          name="translations"
          render={(translationsArray: FieldArrayRenderProps) => {
            return (
              <>
                {translations?.map((item, index: number) => {
                  const isFirstRow = index === 0;

                  return (
                    <div key={index}>
                      <Grid container columnSpacing={12} mb={12}>
                        <Grid xs={6} item>
                          <TextInput
                            data-test-id={`campaign-basic-settings-name-${item?.locale}-input`}
                            label={
                              index === 0 ? (
                                <RequiredFieldLabel
                                  text={message(
                                    { id: "automationNameLocale" },
                                    {
                                      locale: item?.locale,
                                    },
                                  )}
                                />
                              ) : (
                                message(
                                  { id: "automationNameLocale" },
                                  {
                                    locale: item?.locale,
                                  },
                                )
                              )
                            }
                            name={`translations[${index}].name`}
                            width="100%"
                          />
                        </Grid>
                        <Grid xs={isFirstRow ? 6 : 5} item>
                          <TextInput
                            data-test-id={`campaign-basic-settings-name-desc-${item?.locale}-input`}
                            placeholder={message(
                              { id: "automationDescLocale" },
                              {
                                locale: item?.locale,
                              },
                            )}
                            multiline
                            name={`translations[${index}].description`}
                            maxRows={7}
                            width="100%"
                          />
                        </Grid>
                        {!isFirstRow && (
                          <Grid item xs={1} alignSelf="center">
                            <OLClearIcon
                              data-test-id={`campaign-basic-settings-name-remove-${item.locale}-icon`}
                              onClick={() => translationsArray.remove(index)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  );
                })}
                {!areAllLanguagesAdded && (
                  <OLButton
                    color="secondary"
                    style={{
                      padding: "0 8px",
                      marginBottom: "24px",
                    }}
                    onClick={
                      availableLanguages.filter(
                        ({ code }) =>
                          !translations?.find(
                            (openLang) => openLang?.locale === code,
                          ),
                      ).length
                        ? contextMenu.handleMenuOpen
                        : undefined
                    }
                    startIcon={<PlusIcon />}
                    data-test-id="campaign-basic-settings-name-add-translation-button"
                  >
                    {message({ id: "addTranslation" })}
                  </OLButton>
                )}
                <Menu
                  anchorEl={contextMenu.anchor}
                  anchorOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                  onClose={contextMenu.handleMenuClose}
                  open={contextMenu.isMenuOpen}
                  transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                >
                  {availableLanguages
                    .filter(
                      ({ code }: { code: string }) =>
                        !translations?.find(
                          (openLang) => openLang?.locale === code,
                        ),
                    )
                    .map(({ code, name }: { code: string; name: string }) => (
                      <MenuItem
                        key={code}
                        onClick={() => {
                          contextMenu.handleMenuClose();
                          translationsArray.push({
                            description: "",
                            locale: code,
                            name: null,
                          });
                        }}
                      >
                        {name}
                      </MenuItem>
                    ))}
                </Menu>
              </>
            );
          }}
        />
      </>
    );
  },
);
